import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { reportServerBuildId, forceReloadForStaleBundle } from "@/lib/appVersion";

// On an expired/absent session, the app API returns 401. Land the user on /login
// consistently — across BOTH queries and mutations — instead of a blank page or a
// raw thrown error. Exclude /api/auth/* so a bad-password login 401 keeps its error
// and the auth probe / login page don't redirect-loop.
function redirectToLogin() {
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

async function throwIfResNotOk(res: Response) {
  // Stale-bundle check: compare the server's build id (sent on every response)
  // to this bundle's. Runs for ok AND error responses.
  const serverBuildId = res.headers.get("X-App-Version");
  reportServerBuildId(serverBuildId);

  if (!res.ok) {
    if (res.status === 401 && !res.url.includes("/api/auth/")) {
      redirectToLogin();
    } else {
      // Hard-error path: a failed request while the bundle is stale means the
      // user is stuck (their old client is calling a server it no longer
      // matches). Force a one-time reload to recover — no-ops unless there's a
      // real version mismatch, so normal 4xx/5xx on a current bundle are
      // unaffected. redirectToLogin already reloads the bundle, so skip it there.
      forceReloadForStaleBundle(serverBuildId);
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  signal?: AbortSignal,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Is this error worth retrying? (LOGIN-RELIABILITY-1, s163)
 *
 * A 5xx or a network-layer failure is the transport misbehaving — the same request a moment later
 * may well succeed. A 4xx is a real answer from a working server; retrying it only delays the truth
 * (and a genuine 404 must resolve as "no record", not as three doomed attempts).
 *
 * Errors thrown by `throwIfResNotOk` and by the gating fetchers carry the `"<status>: <body>"`
 * shape, so the status is recoverable from the message. No parsable status = no response reached
 * us at all (DNS, connection reset, offline) = transient.
 */
export function isTransientError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  const matched = /^(\d{3}):/.exec(message);
  if (!matched) return true;
  return Number(matched[1]) >= 500;
}

/**
 * Retry policy for GATING queries — the ones whose result decides which view a user sees.
 *
 * ONE retry, not three (s163, corrected). The first version used 3 retries with exponential
 * backoff, which widened the window in which the gating query is unresolved from ~1s to ~7.8s —
 * measured on staging. Every millisecond of that window is a millisecond the app spends unable to
 * answer "is this person alive?", so the window is a cost, not a free safety net. One retry
 * absorbs a single blip; anything worse deserves an honest error the user can act on.
 */
export function gatingRetry(failureCount: number, err: unknown): boolean {
  return failureCount < 1 && isTransientError(err);
}

/** Flat, short backoff. See `gatingRetry` on why the window is kept narrow. */
export function gatingRetryDelay(): number {
  return 500;
}

/**
 * Options every GATING query carries. Exported as one object so a fifth gating read cannot be
 * added with three of the four settings.
 *
 * `networkMode: 'always'` matters more than it looks. Under the default `'online'` mode,
 * query-core's retryer parks a failed query indefinitely when `onlineManager` reports offline
 * (retryer.js: `sleep(delay).then(() => canContinue() ? void 0 : pause())`) — leaving
 * `status:'pending'`, `fetchStatus:'paused'`, `error:null` and no further attempts. That is a
 * query that never reaches `isError`, so an error branch alone can never save the render.
 *
 * 🔴 `'always'` does NOT remove the FOCUS half of that condition — `canContinue()` also requires
 * `focusManager.isFocused()`, so a backgrounded tab still pauses between attempts. That pause is
 * self-healing (it continues on refocus) and the user is not looking at it meanwhile, but it is
 * the reason the render must gate on RESOLUTION rather than on the error state.
 */
export const GATING_QUERY_OPTIONS = {
  retry: gatingRetry,
  retryDelay: gatingRetryDelay,
  networkMode: "always",
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
